import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  TextField,
} from '@mui/material'
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { useParams, useNavigate } from 'react-router-dom'

import { useReceivingStore } from 'store/receiving'
import useReceiveItems from 'lib/services/api/product-service/receiveItems'
import useRequestProducts from 'lib/services/api/product-service/requestProducts'
import { ProcessedProduct } from '../../scan/process-modal/ProcessModal.types'
import type { UndocumentedItem } from 'store/receiving'
import {
  RequestProductVariables,
  ProductRequest,
} from 'lib/services/api/product-service/requestProducts/types'
import AlertDialog from 'components/AlertDialog/AlertDialog'

import styles from './ReviewStep.module.scss'
import type { ReviewStepProps } from './ReviewStep.types'

const ReviewStep: React.FC<ReviewStepProps> = ({ onNext, onBack }) => {
  const undocumentedItems = useReceivingStore((s) => s.undocumentedItems)
  const ownershipType = useReceivingStore((s) => s.ownershipType)
  const productType = useReceivingStore((s) => s.productType)
  const selectedLocationId = useReceivingStore((s) => s.selectedLocationId)
  const resetWorkflow = useReceivingStore((s) => s.resetWorkflow)
  const setUndocumentedItems = useReceivingStore((s) => s.setUndocumentedItems)
  const updateItemQuantity = useReceivingStore((s) => s.updateItemQuantity)

  const { id } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<string | null>(null)
  const [editingQuantities, setEditingQuantities] = useState<
    Record<string, string>
  >({})

  const [receiveItems] = useReceiveItems()
  const [requestProducts] = useRequestProducts()
  const isBiologic = productType === 'Biologic'

  const transformToProcessedProducts = (): ProcessedProduct[] => {
    return undocumentedItems.map((item: UndocumentedItem, index: number) => {
      const hasProductDetails =
        item.catalogNumber ||
        item.deviceId ||
        item.companyName !== 'Unknown Company'

      return {
        index,
        udi: item.udi,
        description: item.deviceDescription,
        productType: item.assetType as any,
        disposition: item.ownershipType as any,
        quantity: item.quantity,
        type: hasProductDetails ? 'existed' : 'new',
        extraCodes: [],
        lotBatch: item.lotBatch,
        serialNumber: item.serialNumber,
        expiryDate: item.expirationDate ? new Date(item.expirationDate) : null,
        manufacturingDate: undefined,
        modelNumber: item.versionModelNumber,
        companyId: undefined,
        vendorName: item.companyName,
        comment: undefined,
        packageIntegrity: true,
        temperature: true,
      }
    })
  }

  const handleSubmitItems = async () => {
    if (undocumentedItems.length === 0) {
      enqueueSnackbar('No items to submit', { variant: 'warning' })
      return
    }

    setIsSubmitting(true)

    const processedItems = transformToProcessedProducts()
    const existedItems = processedItems.filter(
      (item) => item.type === 'existed'
    )
    const newItems = processedItems.filter((item) => item.type === 'new')
    if (existedItems.length) {
      try {
        const processedItemsPayload = existedItems.map((item) => {
          const commonItemProps = {
            disposition: item.disposition || 'Consigned',
            quantity: item.quantity,
            type: item.productType || 'Other',
            udi: item.udi || '',
          }

          if (item.productType === 'Biologic') {
            return {
              ...commonItemProps,
              biologicExtraInfo: {
                comment: item.comment,
                manualExpiration: item.expiryDate,
                packageIntegrity: item.packageIntegrity,
                temperature: item.temperature,
              },
            }
          }

          return commonItemProps
        })

        const response = await receiveItems({
          variables: {
            locationId: Number(selectedLocationId || id),
            items: processedItemsPayload,
          },
        })

        if (response.data?.receiveItems?.message) {
          enqueueSnackbar('Items stored in inventory', {
            variant: 'success',
          })
        }
      } catch (error) {
        enqueueSnackbar('Failed to receive existing items', {
          variant: 'error',
        })
      }
    }

    if (newItems.length) {
      try {
        const processedItemsPayload: RequestProductVariables = {
          locationId: Number(selectedLocationId || id),
          productRequests: newItems.map((item) => {
            const commonItemProps: ProductRequest = {
              disposition: item.disposition || 'Consigned',
              quantity: item.quantity,
              type: item.productType || 'Other',
              uniqueIdentifiers: item.udi || '',
              productDescription: item.description || '',
              expirationDate: item.expiryDate || undefined,
              lotBatch: item.lotBatch || '',
              manufacturingDate: item.manufacturingDate || undefined,
              serialNumber: item.serialNumber || '',
              versionModelNumber: item.modelNumber || '',
              companyId: item.companyId || undefined,
            }

            if (item.productType === 'Biologic') {
              return {
                ...commonItemProps,
                biologicExtraInfo: {
                  comment: item.comment || '',
                  manualExpiration: item.expiryDate || undefined,
                  packageIntegrity: item.packageIntegrity || false,
                  temperature: item.temperature || false,
                },
              }
            }

            return commonItemProps
          }),
        }

        const response = await requestProducts({
          variables: processedItemsPayload,
        })

        if (response.errors) {
          enqueueSnackbar('Something is wrong.', {
            variant: 'error',
          })
        } else {
          enqueueSnackbar('New items requested successfully.', {
            variant: 'success',
          })
        }
      } catch (error) {
        enqueueSnackbar('Failed to request new items', {
          variant: 'error',
        })
      }
    }

    setIsCompleted(true)
    setIsSubmitting(false)

    setTimeout(() => {
      resetWorkflow()
      navigate('/receive')
    }, 2000)
  }

  const handleDeleteItem = (itemId: string) => {
    setItemToDelete(itemId)
  }

  const confirmDeleteItem = () => {
    if (itemToDelete) {
      const next = undocumentedItems.filter(
        (item: UndocumentedItem) => item.id !== itemToDelete
      )
      setUndocumentedItems(next)
      enqueueSnackbar('Item removed', { variant: 'info' })
      setItemToDelete(null)
    }
  }

  const cancelDeleteItem = () => {
    setItemToDelete(null)
  }

  const incrementQuantity = (itemId: string, currentQuantity: number) => {
    setEditingQuantities((prev) => {
      const next = { ...prev }
      delete next[itemId]
      return next
    })
    updateItemQuantity(itemId, currentQuantity + 1)
  }

  const decrementQuantity = (itemId: string, currentQuantity: number) => {
    if (currentQuantity > 1) {
      setEditingQuantities((prev) => {
        const next = { ...prev }
        delete next[itemId]
        return next
      })
      updateItemQuantity(itemId, currentQuantity - 1)
    }
  }

  const handleQuantityInputChange = (itemId: string, value: string) => {
    if (value === '' || /^\d+$/.test(value)) {
      setEditingQuantities((prev) => ({ ...prev, [itemId]: value }))
    }
  }

  const handleQuantityInputCommit = (itemId: string) => {
    const raw = editingQuantities[itemId]
    let nextValue = raw ? parseInt(raw, 10) : NaN
    if (!Number.isFinite(nextValue) || nextValue < 1) {
      nextValue = 1
    }
    setEditingQuantities((prev) => {
      const next = { ...prev }
      delete next[itemId]
      return next
    })
    updateItemQuantity(itemId, nextValue)
  }

  if (isCompleted) {
    return (
      <Box className={`${styles.reviewStep} ${styles.completed}`}>
        <Box className={styles.completionMessage}>
          <Typography variant="h4" className={styles.completedTitle}>
            ✅ Submission Complete!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            All items have been successfully processed and added to inventory.
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            className={styles.completedSubtitle}
          >
            Redirecting to receiving dashboard...
          </Typography>
        </Box>
      </Box>
    )
  }

  const pluralize = (count: number, singular: string, plural?: string) =>
    `${count} ${count === 1 ? singular : plural || `${singular}s`}`

  const totalQty = undocumentedItems.reduce(
    (sum: number, item: UndocumentedItem) => sum + item.quantity,
    0
  )

  return (
    <Box className={styles.reviewStep}>
      <Box className={styles.reviewHeader}>
        <Typography variant="h5" className={styles.title}>
          Review Captured Items
        </Typography>
        <Box className={styles.chipRow}>
          <Chip
            label={`${pluralize(
              undocumentedItems.length,
              'item'
            )} (${totalQty} total qty)`}
            color="primary"
            variant="outlined"
            size="small"
          />
          <Chip
            label={`${ownershipType}`}
            color="secondary"
            variant="outlined"
            size="small"
          />
          <Chip
            label={`${productType}`}
            color="info"
            variant="outlined"
            size="small"
          />
        </Box>
      </Box>

      <Box className={styles.reviewContent}>
        <Card>
          <CardContent>
            <Typography variant="h6" className={styles.sectionTitle}>
              Captured Items ({pluralize(undocumentedItems.length, 'item')},{' '}
              {totalQty} total quantity)
            </Typography>
            {isBiologic && (
              <Typography
                variant="body2"
                color="text.secondary"
                className={styles.biologicNote}
              >
                For biologics, confirm package integrity and temperature.
                Defaults are OK.
              </Typography>
            )}

            {undocumentedItems.length === 0 ? (
              <Box className={styles.emptyState}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  className={styles.emptyText}
                >
                  No items to review. Please go back and capture some items
                  first.
                </Typography>
              </Box>
            ) : (
              <List>
                {undocumentedItems.map(
                  (item: UndocumentedItem, index: number) => (
                    <React.Fragment key={item.id}>
                      <ListItem>
                        <ListItemText
                          primary={
                            <Typography
                              variant="subtitle1"
                              className={styles.itemTitle}
                            >
                              {item.deviceDescription || 'Unknown Product'}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                UDI: {item.udi || 'No UDI'}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Company: {item.companyName}
                              </Typography>
                              {item.catalogNumber && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Catalog: {item.catalogNumber}
                                </Typography>
                              )}
                              {item.lotBatch && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Lot/Batch: {item.lotBatch}
                                </Typography>
                              )}
                              {item.serialNumber && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Serial: {item.serialNumber}
                                </Typography>
                              )}
                              {item.expirationDate && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Expires:{' '}
                                  {new Date(
                                    item.expirationDate
                                  ).toLocaleDateString()}
                                </Typography>
                              )}
                              {isBiologic && (
                                <Box className={styles.biologicControls}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Package Integrity:
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant={
                                      item.packageIntegrity ?? true
                                        ? 'contained'
                                        : 'outlined'
                                    }
                                    onClick={() =>
                                      setUndocumentedItems(
                                        undocumentedItems.map((it) =>
                                          it.id === item.id
                                            ? {
                                                ...it,
                                                packageIntegrity: !(
                                                  item.packageIntegrity ?? true
                                                ),
                                              }
                                            : it
                                        )
                                      )
                                    }
                                  >
                                    {item.packageIntegrity ?? true
                                      ? 'OK'
                                      : 'Not OK'}
                                  </Button>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Temperature:
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant={
                                      item.temperature ?? true
                                        ? 'contained'
                                        : 'outlined'
                                    }
                                    onClick={() =>
                                      setUndocumentedItems(
                                        undocumentedItems.map((it) =>
                                          it.id === item.id
                                            ? {
                                                ...it,
                                                temperature: !(
                                                  item.temperature ?? true
                                                ),
                                              }
                                            : it
                                        )
                                      )
                                    }
                                  >
                                    {item.temperature ?? true ? 'OK' : 'Not OK'}
                                  </Button>
                                </Box>
                              )}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Captured:{' '}
                                {new Date(item.scannedAt).toLocaleString()}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction className={styles.qtyAction}>
                          <Box className={styles.qtyBox}>
                            <Typography
                              variant="caption"
                              className={styles.qtyLabel}
                            >
                              Qty:
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() =>
                                decrementQuantity(item.id, item.quantity)
                              }
                              disabled={item.quantity <= 1}
                              className={styles.qtyIcon}
                            >
                              <RemoveIcon className={styles.qtyIconSvg} />
                            </IconButton>
                            <TextField
                              size="small"
                              type="text"
                              inputMode="numeric"
                              value={
                                editingQuantities[item.id] ??
                                String(item.quantity)
                              }
                              onChange={(e) =>
                                handleQuantityInputChange(
                                  item.id,
                                  e.target.value
                                )
                              }
                              onBlur={() => handleQuantityInputCommit(item.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.currentTarget.blur()
                                }
                              }}
                              inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                min: 1,
                              }}
                              className={styles.qtyInput}
                            />
                            <IconButton
                              size="small"
                              onClick={() =>
                                incrementQuantity(item.id, item.quantity)
                              }
                              className={styles.qtyIcon}
                            >
                              <AddIcon className={styles.qtyIconSvg} />
                            </IconButton>
                          </Box>

                          <IconButton
                            onClick={() => handleDeleteItem(item.id)}
                            color="error"
                            size="small"
                            className={styles.deleteIcon}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < undocumentedItems.length - 1 && <Divider />}
                    </React.Fragment>
                  )
                )}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>

      <Box className={styles.bottomNavigation}>
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={isSubmitting}
          sx={{ minWidth: 100 }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitItems}
          disabled={undocumentedItems.length === 0 || isSubmitting}
          sx={{ minWidth: 160 }}
          startIcon={isSubmitting && <CircularProgress size={20} />}
        >
          {isSubmitting
            ? 'Submitting...'
            : `Submit (${pluralize(
                undocumentedItems.length,
                'item'
              )}, ${totalQty} qty)`}
        </Button>
      </Box>

      <AlertDialog
        isOpen={!!itemToDelete}
        mode="warning"
        title="Confirm Deletion"
        description={
          itemToDelete ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Are you sure you want to remove this item from the review?
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {undocumentedItems.find(
                  (item: UndocumentedItem) => item.id === itemToDelete
                )?.deviceDescription || 'Unknown Product'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This action cannot be undone.
              </Typography>
            </Box>
          ) : (
            'Are you sure you want to remove this item from the review? This action cannot be undone.'
          )
        }
        primaryButtonText="Delete"
        primaryButtonAction={confirmDeleteItem}
        secondaryButtonText="Cancel"
        secondaryButtonAction={cancelDeleteItem}
        showIcon={true}
      />
    </Box>
  )
}

export default ReviewStep
